"use client";

import React, { useState } from "react";
import {
    Box,
    CircularProgress,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableBody,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Alert,
} from "@mui/material";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import PeerGroupSelect from "@/components/PeerGroupSelect";
import PageTitle from "@/components/PageTitle";
import { usePrintFilters } from "@/hooks/usePrintFilters";
import { useDealership } from "@/contexts/DealershipContext";
import { formatCurrency, formatPercent, formatYears } from "@/utils/valueFormatters";
import { getApiUrl } from "@/utils/api";
import { fetcher } from "@/utils/fetch";
import RetentionProfileChart from "@/components/RetentionProfileChart";
import DemographicComparisonChart from "@/components/DemographicComparisonChart";
import TurnoverByDemographicChart from "@/components/TurnoverByDemographicChart";
import TurnoverMonthlyChart from "@/components/TurnoverMonthlyChart";
import { exportExecutiveSummaryData } from "@/utils/exportExecutiveSummary";

import RankingDisplay from "@/components/RankingDisplay";
import { TourViewTarget, useTourSteps } from "@/utils/useTourSteps";
import { Typography } from "@/components/wrappers/Typography";
import { TableCell } from "@/components/wrappers/TableCell";

type ExecutiveSummaryYourDealershipValues = {
    annualized_turnover: string;
    one_year_retention: number;
    three_year_retention: number;
    average_compensation: number | null;
    average_tenure: number | null;
    median_tenure: number | null;
    annualized_turnover_rank: number | null;
    one_year_retention_rank: number | null;
    three_year_retention_rank: number | null;
    average_compensation_rank: number | null;
    average_tenure_rank: number | null;
    median_tenure_rank: number | null;
};

type ExecutiveSummaryValues = {
    annualized_turnover: string;
    one_year_retention: number;
    three_year_retention: number;
    average_compensation: number | null;
    average_tenure: number | null;
    median_tenure: number | null;
};

type ExecutiveSummary = {
    your_dealership: ExecutiveSummaryYourDealershipValues;
    your_peers: ExecutiveSummaryValues;
    all_dealerships: ExecutiveSummaryValues;
};

type RetentionData = {
    [key: string]: number;
};

type RetentionProfile = {
    your_dealership: RetentionData;
    your_peers: RetentionData;
    all_dealerships: RetentionData;
};

type DemographicData = {
    gender: {
        male: number;
        female: number;
    };
    generation: {
        babyBoomer: number;
        genX: number;
        genY: number;
        genZ: number;
    };
};

type DemographicComparison = {
    your_dealership: DemographicData;
    your_peers: DemographicData;
    all_dealerships: DemographicData;
};

type TurnoverDemographicData = {
    all: number;
    gender: {
        male: number;
        female: number;
    };
    generation: {
        babyBoomer: number;
        genX: number;
        genY: number;
        genZ: number;
    };
};

type TurnoverByDemographic = {
    your_dealership: TurnoverDemographicData;
    your_peers: TurnoverDemographicData;
    all_dealerships: TurnoverDemographicData;
};

type MonthlyTurnover = {
    peer_group_monthly: { [key: string]: number };
    peer_group_rolling: { [key: string]: number | null };
    your_dealership_monthly: { [key: string]: number };
    your_dealership_rolling: { [key: string]: number | null };
    all_dealerships_monthly: { [key: string]: number };
    all_dealerships_rolling: { [key: string]: number | null };
};

export default function DashboardPage() {
    // Subscribe to i18n changes to re-render when language changes
    const { t } = useTranslation();

    const {
        metadata,
        participantInfo,
        isMetadataLoading,
        isParticipantLoading,
        metadataError,
        participantError,
        userInfo,
        isUserInfoLoading,
        userInfoError,
        selectedPeerGroup,
        positionGroups,
        isPositionGroupsLoading,
        positionGroupsError,
    } = useDealership();

    const [selectedPositionGroup, setSelectedPositionGroup] = useState(1);

    const effectivePeerGroup =
        selectedPeerGroup ||
        Object.entries(participantInfo?.peer_groups || {}).find(
            ([, info]) => info.peer_group_default
        )?.[0];

    const participantId = userInfo?.participant_id;

    const {
        data: dashboardDataForAllPositions,
        error: dashboardError,
        isLoading: isDashboardLoading,
    } = useSWR(
        participantId && effectivePeerGroup
            ? `${getApiUrl(
                  "/dashboard"
              )}?participant_id=${participantId}&peer_group_criteria=${participantInfo?.peer_groups[effectivePeerGroup]?.peer_group_criteria}`
            : null,
        fetcher
    );

    // Only initialize tour after all data is loaded and UI is rendered
    const shouldInitializeTour =
        !isMetadataLoading &&
        !isParticipantLoading &&
        !isUserInfoLoading &&
        !isPositionGroupsLoading &&
        !isDashboardLoading &&
        metadata &&
        participantInfo &&
        userInfo &&
        positionGroups &&
        !metadataError &&
        !participantError &&
        !userInfoError &&
        !positionGroupsError &&
        !dashboardError;

    useTourSteps(shouldInitializeTour ? TourViewTarget.EXEC_SUMMARY_TAB : undefined);

    const printFilters = usePrintFilters();

    if (isParticipantLoading || isUserInfoLoading || isMetadataLoading || isPositionGroupsLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    width: "100vw",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (metadataError || participantError || userInfoError || positionGroupsError) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    width: "100vw",
                }}
            >
                <Alert severity="error">Error loading dealership data. Please try again.</Alert>
            </Box>
        );
    }

    if (!metadata || !participantInfo || !userInfo || !positionGroups) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    width: "100vw",
                }}
            >
                <Alert severity="warning">No dealership data available.</Alert>
            </Box>
        );
    }

    const dashboardData = dashboardDataForAllPositions?.[selectedPositionGroup];

    const executiveSummary: ExecutiveSummary = dashboardData?.executive_summary;
    const retentionProfile: RetentionProfile = dashboardData?.retention_profile;
    const demographicComparison: DemographicComparison = dashboardData?.demographic_comparison;
    const turnoverByDemographic: TurnoverByDemographic = dashboardData?.turnover_by_demographic;
    const monthlyTurnover: MonthlyTurnover = dashboardData?.monthly_turnover;

    const selectedPositionsGroup = positionGroups.find(
        (group) => group.position_group_id === selectedPositionGroup
    );

    const tableData = executiveSummary
        ? [
              {
                  metric: t("common.metrics.avgCompensation"),
                  position: t(`jobTitles.${selectedPositionsGroup?.position_group_description || "All Positions"}`, selectedPositionsGroup?.position_group_description || "All Positions"),
                  your_dealership: formatCurrency(
                      executiveSummary.your_dealership.average_compensation
                  ),
                  your_peers: formatCurrency(executiveSummary.your_peers.average_compensation),
                  all_dealerships: formatCurrency(
                      executiveSummary.all_dealerships.average_compensation
                  ),
                  ranking:
                      typeof executiveSummary.your_dealership.average_compensation_rank ===
                      "number" ? (
                          <RankingDisplay
                              rank={executiveSummary.your_dealership.average_compensation_rank}
                          />
                      ) : (
                          "-"
                      ),
                  rankingValue: executiveSummary.your_dealership.average_compensation_rank,
              },
              {
                  metric: t("common.metrics.avgTenure"),
                  position: t(`jobTitles.${selectedPositionsGroup?.position_group_description || "All Positions"}`, selectedPositionsGroup?.position_group_description || "All Positions"),
                  your_dealership: formatYears(executiveSummary.your_dealership.average_tenure),
                  your_peers: formatYears(executiveSummary.your_peers.average_tenure),
                  all_dealerships: formatYears(executiveSummary.all_dealerships.average_tenure),
                  ranking:
                      typeof executiveSummary.your_dealership.average_tenure_rank === "number" ? (
                          <RankingDisplay
                              rank={executiveSummary.your_dealership.average_tenure_rank}
                          />
                      ) : (
                          "—"
                      ),
                  rankingValue: executiveSummary.your_dealership.average_tenure_rank,
              },
              {
                  metric: t("common.metrics.medianTenure"),
                  position: t(`jobTitles.${selectedPositionsGroup?.position_group_description || "All Positions"}`, selectedPositionsGroup?.position_group_description || "All Positions"),
                  your_dealership: formatYears(executiveSummary.your_dealership.median_tenure),
                  your_peers: formatYears(executiveSummary.your_peers.median_tenure),
                  all_dealerships: formatYears(executiveSummary.all_dealerships.median_tenure),
                  ranking:
                      typeof executiveSummary.your_dealership.median_tenure_rank === "number" ? (
                          <RankingDisplay
                              rank={executiveSummary.your_dealership.median_tenure_rank}
                          />
                      ) : (
                          "—"
                      ),
                  rankingValue: executiveSummary.your_dealership.median_tenure_rank,
              },
              {
                  metric: t("common.metrics.oneYearRetention"),
                  position: t(`jobTitles.${selectedPositionsGroup?.position_group_description || "All Positions"}`, selectedPositionsGroup?.position_group_description || "All Positions"),
                  your_dealership: formatPercent(
                      executiveSummary.your_dealership.one_year_retention === null
                          ? null
                          : executiveSummary.your_dealership.one_year_retention * 100,
                      1
                  ),
                  your_peers: formatPercent(
                      executiveSummary.your_peers.one_year_retention === null
                          ? null
                          : executiveSummary.your_peers.one_year_retention * 100,
                      1
                  ),
                  all_dealerships: formatPercent(
                      executiveSummary.all_dealerships.one_year_retention === null
                          ? null
                          : executiveSummary.all_dealerships.one_year_retention * 100,
                      1
                  ),
                  ranking:
                      typeof executiveSummary.your_dealership.one_year_retention_rank ===
                      "number" ? (
                          <RankingDisplay
                              rank={executiveSummary.your_dealership.one_year_retention_rank}
                          />
                      ) : (
                          "—"
                      ),
                  rankingValue: executiveSummary.your_dealership.one_year_retention_rank,
              },
              {
                  metric: t("common.metrics.threeYearRetention"),
                  position: t(`jobTitles.${selectedPositionsGroup?.position_group_description || "All Positions"}`, selectedPositionsGroup?.position_group_description || "All Positions"),
                  your_dealership: formatPercent(
                      executiveSummary.your_dealership.three_year_retention === null
                          ? null
                          : executiveSummary.your_dealership.three_year_retention * 100,
                      1
                  ),
                  your_peers: formatPercent(
                      executiveSummary.your_peers.three_year_retention === null
                          ? null
                          : executiveSummary.your_peers.three_year_retention * 100,
                      1
                  ),
                  all_dealerships: formatPercent(
                      executiveSummary.all_dealerships.three_year_retention === null
                          ? null
                          : executiveSummary.all_dealerships.three_year_retention * 100,
                      1
                  ),
                  ranking:
                      typeof executiveSummary.your_dealership.three_year_retention_rank ===
                      "number" ? (
                          <RankingDisplay
                              rank={executiveSummary.your_dealership.three_year_retention_rank}
                          />
                      ) : (
                          "—"
                      ),
                  rankingValue: executiveSummary.your_dealership.three_year_retention_rank,
              },
              {
                  metric: t("common.metrics.annualizedTurnover"),
                  position: t(`jobTitles.${selectedPositionsGroup?.position_group_description || "All Positions"}`, selectedPositionsGroup?.position_group_description || "All Positions"),
                  your_dealership: formatPercent(
                      executiveSummary.your_dealership.annualized_turnover === null
                          ? null
                          : parseFloat(executiveSummary.your_dealership.annualized_turnover) * 100,
                      1
                  ),
                  your_peers: formatPercent(
                      executiveSummary.your_peers.annualized_turnover === null
                          ? null
                          : parseFloat(executiveSummary.your_peers.annualized_turnover) * 100,
                      1
                  ),
                  all_dealerships: formatPercent(
                      executiveSummary.all_dealerships.annualized_turnover === null
                          ? null
                          : parseFloat(executiveSummary.all_dealerships.annualized_turnover) * 100,
                      1
                  ),
                  ranking:
                      typeof executiveSummary.your_dealership.annualized_turnover_rank ===
                      "number" ? (
                          <RankingDisplay
                              rank={executiveSummary.your_dealership.annualized_turnover_rank}
                          />
                      ) : (
                          "—"
                      ),
                  rankingValue: executiveSummary.your_dealership.annualized_turnover_rank,
              },
          ]
        : [];

    const handleExport = (filetype?: string) => {
        if (!dashboardData || !tableData?.length) {
            console.warn("No data available for export");
            return;
        }
        exportExecutiveSummaryData(tableData, filetype);
    };

    return (
        <Box
            sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                "@media print": {
                    height: "auto",
                    overflow: "visible",
                },
            }}
        >
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 2,
                    overflowY: "auto",
                    "@media print": {
                        overflow: "visible",
                        height: "auto",
                        padding: 0,
                    },
                }}
            >
                <PageTitle
                    titleTx="executiveSummary.title"
                    subtitleTx="common.variants.subtitle"
                    onExport={handleExport}
                    printFilters={printFilters}
                />

                {/* Screen-only form controls */}
                <Box className="screen-only" sx={{ display: "flex", gap: 3, mb: 3 }}>
                    <PeerGroupSelect width="50%" />
                    <Paper
                        className="exec-step-3"
                        variant="outlined"
                        sx={{ p: 2, borderRadius: 4, width: "50%" }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography
                                tx="common.entities.positions"
                                variant="h8"
                                component="h3"
                            />
                        </Box>
                        <FormControl fullWidth margin="normal" size="small">
                            <InputLabel className="screen-only" id="position-group-select-label">
                                {t("common.entities.position")}
                            </InputLabel>
                            <Select
                                labelId="position-group-select-label"
                                id="position-group-select"
                                value={selectedPositionGroup}
                                onChange={(event) =>
                                    setSelectedPositionGroup(Number(event.target.value))
                                }
                                label={t("common.entities.position")}
                                displayEmpty
                            >
                                {positionGroups.map((group) => (
                                    <MenuItem
                                        key={group.position_group_id}
                                        value={group.position_group_id}
                                    >
                                        {t(`jobTitles.${group.position_group_description}`, group.position_group_description)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Paper>
                </Box>

                {(() => {
                    if (isDashboardLoading) {
                        return (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "50vh",
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        );
                    }

                    if (dashboardError) {
                        return <Alert severity="error">{t("errors.dashboardDataLoad")}</Alert>;
                    }

                    if (!dashboardData) {
                        return <Alert severity="error">{t("errors.noDataSelection")}</Alert>;
                    }

                    return (
                        <>
                            <TableContainer
                                sx={{ pageBreakAfter: "always" }}
                                className="exec-step-4"
                                component={Paper}
                                elevation={0}
                            >
                                <Table
                                    sx={{ minWidth: 650 }}
                                    aria-label="dashboard comparison table"
                                >
                                    <TableHead sx={{ bgcolor: "white" }}>
                                        <TableRow sx={{ color: "#000", fontWeight: 600 }}>
                                            <TableCell
                                                tx="executiveSummary.headers.keyEmploymentMetrics"
                                                sx={{ color: "#000", fontWeight: 600 }}
                                            />
                                            <TableCell
                                                tx="common.entities.position"
                                                sx={{ color: "#000", fontWeight: 600 }}
                                            />
                                            <TableCell
                                                tx="common.comparisonGroups.yourDealership"
                                                align="center"
                                                sx={{ color: "#000", fontWeight: 600 }}
                                            />
                                            <TableCell
                                                tx="common.comparisonGroups.yourPeers"
                                                align="center"
                                                sx={{ color: "#000", fontWeight: 600 }}
                                            />
                                            <TableCell
                                                tx="common.comparisonGroups.allDealerships"
                                                align="center"
                                                sx={{ color: "#000", fontWeight: 600 }}
                                            />
                                            <TableCell
                                                tx="executiveSummary.headers.ranking"
                                                align="center"
                                                sx={{ color: "#000", fontWeight: 600 }}
                                            />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableData.map((row) => (
                                            <TableRow
                                                key={row.metric}
                                                sx={{
                                                    "&:last-child td, &:last-child th": {
                                                        border: 0,
                                                    },
                                                    "&:nth-of-type(odd)": { bgcolor: "white" },
                                                }}
                                            >
                                                <TableCell component="th" scope="row">
                                                    {row.metric}
                                                </TableCell>
                                                <TableCell>{row.position}</TableCell>
                                                <TableCell align="center">
                                                    {row.your_dealership}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {row.your_peers}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {row.all_dealerships}
                                                </TableCell>
                                                <TableCell
                                                    className="exec-step-5"
                                                    align="center"
                                                    sx={{
                                                        display: "table-cell",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {row.ranking}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ display: "flex", flexDirection: "column", mt: 3, gap: 2 }}>
                                <Typography
                                    className="screen-only"
                                    variant="h6"
                                    component="h2"
                                    sx={{ mb: 1 }}
                                    tx="executiveSummary.sections.turnoverComparison"
                                />
                                <Box className="chart-section">
                                    <RetentionProfileChart data={retentionProfile} />
                                </Box>
                                <Box className="chart-section">
                                    <TurnoverMonthlyChart data={monthlyTurnover} />
                                </Box>
                                <Box className="chart-section">
                                    <DemographicComparisonChart data={demographicComparison} />
                                </Box>
                                <Box className="chart-section">
                                    <TurnoverByDemographicChart data={turnoverByDemographic} />
                                </Box>
                            </Box>
                        </>
                    );
                })()}
            </Box>
        </Box>
    );
}
